// CascadeMenu — a reusable, multi-level (nested) context menu for the canvas
// dropdown plugin. Hand the result of `cascadeMenu(items)` to the opener:
//
//   el.call('openDropdown', { id, anchorRect, content: cascadeMenu(items) })
//
// Item shape:
//   { label, icon?, hint?, danger?, ariaLabel?, title?, onClick?(e, el, s),
//     children?: [ ...nested items ],   // recursive cascade (any depth)
//     submenuBody?: { ...DOMQL menu rows } }  // OR drop in an existing body
//   { divider: true }                         // a separator row
//
// An item with `children` / `submenuBody` renders a ▸ and reveals a flyout
// submenu — a NEW absolutely-positioned panel off its own row — on hover/focus.
// Reveal is pure CSS (`:hover > [data-submenu]`), so nesting is fully general:
// every level reuses this same builder and reveals independently of the others.
//
// Two gotchas this handles:
//  - A parent row is a <div>, NOT a <button>: its flyout contains buttons, and
//    button-in-button is invalid HTML (the browser drops the nested buttons).
//    Leaf (action) rows stay <button>s.
//  - The dropdown plugin's panel is `overflow: auto`, which clips a flyout that
//    sits at `left: 100%`. The body clears the panel's overflow on mount.

// Build a menu body (glass card chrome from the shared CanvasCreateMenuBody)
// holding one row per item. Recursion happens inside cascadeRow for submenus.
//
// FA204 / frank trap — `cascadeRow` (+ its `CASCADE_ROW` chrome constant)
// used to be module-scope, read from this function's own body: an exported
// function reading a module-scope sibling is exactly as unresolvable post
// frank.toJSON as a component reading a module-scope const (workspace
// d6ea1142 / 8732aa07, project_frank_drops_module_local_helpers). Both now
// live as self-contained locals INSIDE `cascadeMenu`, the one consumer —
// `cascadeRow`'s own recursive call into `cascadeMenu` for submenus is safe
// (a named function referencing its own binding is intrinsic to the
// function's text, not a module-scope resolution).
export function cascadeMenu(items) {
  // STUDIO-NEW-1 (2026-08-14): the flyout's reveal is pure CSS
  // (`:hover > [data-submenu]`), so — unlike the top-level dropdown Panel,
  // which flips side via computePosition.js — it has zero notion of the
  // viewport edge. `left: 100%` from a row anchored in the right/bottom
  // quadrant of the screen (the common case: the CanvasCreateIsland "+"
  // trigger sits at the far right edge by design, and a right-click
  // context menu can land anywhere the user clicks) pushes the flyout
  // mostly or entirely past the browser edge. Reproduced live: the
  // "New" flyout rendered as an unreachable sliver past the right
  // viewport edge, and clicking the coordinates devtools reported for
  // "Create project" landed outside the (invisible, off-canvas) row and
  // fell through to the outside-click handler — closing the menu instead
  // of opening the picker.
  //
  // Fix: measure on reveal (mouseenter / focus — by then the browser has
  // just applied the :hover/:focus-within `display:flex`, so a
  // synchronous getBoundingClientRect() reflects the flyout's real,
  // laid-out size) and flip to whichever side/edge actually has room.
  // Kept as a LOCAL closure (not module-scope) — a module-scope helper
  // read by this exported function is unresolvable after frank.toJSON
  // (see the cascadeRow/CASCADE_ROW note below); this is the same fix
  // shape, one level up.
  const FLYOUT_EDGE_PAD = 8
  function flipSubmenuIntoView(rowNode) {
    if (typeof window === 'undefined' || !rowNode) return
    const sub = rowNode.querySelector('[data-submenu="menu"]')
    if (!sub) return
    // Reset to the CSS default before measuring — otherwise a stale flip
    // from a previous hover (before a resize, say) would measure its own
    // already-flipped box instead of the natural one.
    sub.style.left = ''
    sub.style.right = ''
    sub.style.marginLeft = ''
    sub.style.marginRight = ''
    sub.style.top = ''
    sub.style.bottom = ''
    const subRect = sub.getBoundingClientRect()
    const rowRect = rowNode.getBoundingClientRect()
    if (rowRect.right + subRect.width + FLYOUT_EDGE_PAD > window.innerWidth) {
      sub.style.left = 'auto'
      sub.style.right = '100%'
      sub.style.marginLeft = '0'
      sub.style.marginRight = 'var(--spacing-v)'
    }
    if (rowRect.top + subRect.height + FLYOUT_EDGE_PAD > window.innerHeight) {
      sub.style.top = 'auto'
      sub.style.bottom = '-6px'
    }
  }

  // Row chrome shared by every item — mirrors the /files + /studio context
  // rows (MenuPanelItem/ctxMenuRow look) so the menu reads as the house
  // dropdown.
  const CASCADE_ROW = {
    role: 'menuitem',
    position: 'relative',
    display: 'flex',
    flow: 'x',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'Z',
    width: '100%',
    padding: 'Y Z',
    round: 'radiusControl',
    border: '0',
    background: 'transparent',
    textAlign: 'left',
    fontSize: 'Z2',
    cursor: 'pointer',
    flexShrink: '0',
    whiteSpace: 'nowrap'
  }

  function cascadeRow(item) {
    const kids = Array.isArray(item.children) && item.children.length ? item.children : null
    const hasSub = !!(kids || item.submenuBody)
    const row = {
      ...CASCADE_ROW,
      // Parent rows are <div>s (hover containers — their flyout holds buttons, and
      // button-in-button is invalid); leaf actions are <button>s.
      tag: hasSub ? 'div' : 'button',
      ...(hasSub ? { tabindex: '0' } : { type: 'button' }),
      'aria-label': item.ariaLabel || item.label,
      ...(item.title ? { title: item.title } : {}),
      // Was `'white'` for normal rows — a theme-INVARIANT literal, so every
      // cascade row rendered white-on-light in the light shell, i.e. invisible.
      // `title` is the adaptive ink token ContextDropdown already uses for the
      // same rows, and the hover tint moves onto `overlay` so it reads in BOTH
      // themes instead of only over a dark panel.
      color: 'title',
      // A `danger` item is THE house danger row, the same three parts
      // MenuPanelItem and ContextDropdownItem render: `data-danger` (a FLAT
      // `dataDanger`), `dangerInk` at rest keyed on it, and the brand error
      // fill pair under the pointer, on hover AND press. It was the raw
      // palette `red` with a `red.15` hover tint and no press state. The
      // danger row carries no plain `:hover` wash, so no rule of equal
      // specificity can win over the fill pair by stylesheet order.
      ...(item.danger
        ? {
            dataDanger: true,
            '&[data-danger]': { color: 'dangerInk' },
            '&[data-danger]:hover': { background: 'errorFillBg', color: 'errorFillInk' },
            '&[data-danger]:active': { background: 'errorFillBg', color: 'errorFillInk' }
          }
        : {
            ':hover': {
              background: 'overlay'
            }
          }),
      // THE house ring, inset (the panel clips) — same token MenuPanelItem
      // uses; was a raw px + rgba blue outline, a third ring style (F1).
      ':focus-visible': { shadow: 'focusRingInset' },

      // Left side — icon + label, ellipsised so a long label never widens the row.
      Main: {
        display: 'flex',
        flow: 'x',
        alignItems: 'center',
        gap: 'Y',
        minWidth: '0',
        ...(item.icon
          ? {
              Icon: {
                extends: 'Icon',
                icon: item.icon,
                boxSize: 'A',
                // Same fix as the row's own `color` above — 'white.65' was
                // invisible in the light shell.
                color: 'title.65',
                flexShrink: '0'
              }
            }
          : {}),
        Label: { tag: 'span', text: item.label, overflow: 'hidden', textOverflow: 'ellipsis' }
      }
    }

    // Leaf actions fire onClick; parents (with a submenu) only reveal the flyout.
    if (item.onClick && !hasSub) {
      row.onClick = (e, el, s) => {
        e.stopPropagation()
        item.onClick(e, el, s)
      }
    }

    if (hasSub) {
      // Keep the parent hovered while the pointer crosses the visual gap to the
      // flyout. Without this transparent bridge, marginLeft creates a dead zone
      // that closes the pure-CSS submenu before the pointer can reach it.
      // Mirrored on ::before (left side) too — flipSubmenuIntoView can flip the
      // flyout to open leftward when the row is near the right viewport edge
      // (STUDIO-NEW-1), and a bridge only on the right would leave a dead zone
      // on the side the flyout actually opened on.
      row['::after'] = {
        content: '""',
        position: 'absolute',
        left: '100%',
        top: '-V',
        bottom: '-V',
        width: 'V'
      }
      row['::before'] = {
        content: '""',
        position: 'absolute',
        right: '100%',
        top: '-V',
        bottom: '-V',
        width: 'V'
      }
      row.Chevron = {
        tag: 'span',
        text: '▸',
        fontSize: 'Z1',
        color: 'title.4',
        flexShrink: '0'
      }
      // Reveal THIS row's own flyout (direct child only → each level independent)
      // and measure/flip it into the viewport (STUDIO-NEW-1 — see
      // flipSubmenuIntoView above for why the CSS reveal alone isn't enough).
      row[':hover > [data-submenu]'] = { display: 'flex' }
      row[':focus-within > [data-submenu]'] = { display: 'flex' }
      row.onMouseenter = (e, el) => flipSubmenuIntoView(el.node)
      row.onFocus = (e, el) => flipSubmenuIntoView(el.node)
      row.Submenu = {
        ...(kids
          ? cascadeMenu(kids)
          : { extends: 'CanvasCreateMenuBody', flow: 'y', gap: '2px', ...item.submenuBody }),
        attr: { 'data-submenu': 'menu' },
        position: 'absolute',
        left: '100%',
        top: '-6px',
        marginLeft: 'V',
        display: 'none',
        zIndex: '40',
        overflow: 'visible',
        // The flyout is absolutely positioned inside its ROW, so its width is
        // computed against the row's narrow containing block: MenuPanel's
        // inherited maxWidth:100% clamped it to the row, minWidth:220px floored
        // it back up, and the CanvasCreateMenuItem hint lines ("Slide deck
        // (Symbols deck template).", …) spilled past the 220px panel edge.
        // Release the inherited clamp + size the flyout to its own widest row.
        // (2026-07-03 per Nika "context menu > New > menu has texts out of menu".)
        width: 'max-content',
        maxWidth: 'none'
      }
    } else if (item.hint) {
      row.Hint = {
        tag: 'span',
        text: item.hint,
        fontSize: 'Z1',
        color: 'title.3',
        flexShrink: '0'
      }
    }

    return row
  }

  const body = {
    extends: 'CanvasCreateMenuBody',
    flow: 'y',
    gap: '2px',
    overflow: 'visible',
    // The dropdown plugin's panel is overflow:auto — that clips a flyout sitting
    // at left:100%. Clear it on mount so the cascade can escape the panel box.
    onRender: (el) => {
      const panel = el.node && el.node.closest && el.node.closest('[data-smbls-dropdown="panel"]')
      if (panel) panel.style.overflow = 'visible'
    }
  }
  ;(items || []).forEach((item, i) => {
    if (!item) return
    if (item.divider) {
      body['D' + i] = { extends: 'Line', role: 'separator', margin: 'V 0' }
    } else {
      body['R' + i] = cascadeRow(item)
    }
  })
  return body
}
